import {
  Archive,
  Check,
  Clipboard,
  ExternalLink,
  Pencil,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

import {
  archiveStory,
  generateDrafts,
  loadStoryBundle,
  saveDraft,
} from "@/lib/newsroom";
import type {
  Draft,
  DraftProvider,
  NewsroomSettings,
  Story,
  StoryBundle,
} from "@/lib/types";

const variantLabels = {
  short_a: "Short",
  short_b: "Short",
  long: "Long",
} as const;

function formatDate(value: string | null): string {
  if (!value) {
    return "Time unavailable";
  }
  return new Intl.DateTimeFormat("en", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

export function StoryDetail({
  story,
  settings,
  bundleVersion,
  onChanged,
}: {
  story: Story | null;
  settings: NewsroomSettings | null;
  bundleVersion: number;
  onChanged: () => Promise<void>;
}) {
  const [bundle, setBundle] = useState<StoryBundle | null>(null);
  const [bundleStoryId, setBundleStoryId] = useState<string | null>(null);
  const [draftBodies, setDraftBodies] = useState<Record<string, string>>({});
  const [editingDraftId, setEditingDraftId] = useState<string | null>(null);
  const [generatedStoryId, setGeneratedStoryId] = useState<string | null>(null);
  const [busyAction, setBusyAction] = useState<string | null>(null);
  const [message, setMessage] = useState("");
  const activeStoryIdRef = useRef<string | null>(story?.id ?? null);
  const requestVersionRef = useRef(0);
  activeStoryIdRef.current = story?.id ?? null;

  async function refreshBundle(clear = false) {
    const requestedStoryId = activeStoryIdRef.current;
    const requestVersion = requestVersionRef.current + 1;
    requestVersionRef.current = requestVersion;
    if (clear) {
      setBundle(null);
      setBundleStoryId(null);
      setDraftBodies({});
      setEditingDraftId(null);
    }
    if (!requestedStoryId) {
      return;
    }
    const next = await loadStoryBundle(requestedStoryId);
    if (
      requestVersionRef.current !== requestVersion ||
      activeStoryIdRef.current !== requestedStoryId
    ) {
      return;
    }
    setBundle(next);
    setBundleStoryId(requestedStoryId);
    setDraftBodies(
      Object.fromEntries(next.drafts.map((draft) => [draft.id, draft.body])),
    );
  }

  useEffect(() => {
    setMessage("");
    setGeneratedStoryId(null);
    const requestedStoryId = story?.id ?? null;
    void refreshBundle(true).catch((error: unknown) => {
      if (activeStoryIdRef.current === requestedStoryId) {
        setMessage(
          error instanceof Error ? error.message : "Unable to load story",
        );
      }
    });
    return () => {
      requestVersionRef.current += 1;
    };
  }, [story?.id]);

  useEffect(() => {
    if (!story?.id || bundleStoryId !== story.id) {
      return;
    }
    void refreshBundle().catch((error: unknown) => {
      if (activeStoryIdRef.current === story.id) {
        setMessage(
          error instanceof Error ? error.message : "Unable to refresh story",
        );
      }
    });
  }, [bundleVersion]);

  const provider: DraftProvider =
    settings?.preferred_provider ?? "deterministic";
  const model =
    provider === "openai"
      ? settings?.openai_model
      : provider === "openrouter"
        ? settings?.openrouter_model
        : undefined;
  const activeBundle = bundleStoryId === story?.id ? bundle : null;
  const displayedDrafts = useMemo(
    () => {
      if (generatedStoryId !== story?.id) {
        return [];
      }
      return (["short_a", "long"] as const)
        .map((variant) =>
          activeBundle?.drafts.find((draft) => draft.variant === variant)
        )
        .filter((draft): draft is Draft => Boolean(draft));
    },
    [activeBundle?.drafts, generatedStoryId, story?.id],
  );

  if (!story) {
    return (
      <section className="detail-empty">
        <ShieldCheck aria-hidden="true" />
        <h2>Select a story.</h2>
        <p>Review the source, then edit or copy the short and long drafts.</p>
      </section>
    );
  }
  const selectedStory = story;

  async function generate() {
    setBusyAction("regenerate");
    setEditingDraftId(null);
    setMessage("");
    try {
      await generateDrafts(selectedStory.id, provider, model);
      if (activeStoryIdRef.current !== selectedStory.id) {
        return;
      }
      await refreshBundle();
      setGeneratedStoryId(selectedStory.id);
      setMessage(
        provider === "deterministic"
          ? "Short and long drafts generated."
          : `Drafts generated with ${provider}.`,
      );
    } catch (error) {
      setMessage(
        error instanceof Error ? error.message : "Draft generation failed",
      );
    } finally {
      setBusyAction(null);
    }
  }

  async function finishEditing(draft: Draft) {
    if (!activeBundle?.drafts.some((current) => current.id === draft.id)) {
      return;
    }
    setBusyAction(`save-${draft.id}`);
    setMessage("");
    try {
      await saveDraft(draft.id, draftBodies[draft.id] ?? draft.body);
      setEditingDraftId(null);
      setMessage("Edit applied.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Unable to save draft");
    } finally {
      setBusyAction(null);
    }
  }

  async function copy(draft: Draft) {
    if (!activeBundle?.drafts.some((current) => current.id === draft.id)) {
      return;
    }
    await navigator.clipboard.writeText(draftBodies[draft.id] ?? draft.body);
    setMessage("Copied. Review the final wording before publishing manually.");
  }

  async function archive() {
    setBusyAction("archive");
    try {
      await archiveStory(selectedStory.id);
      await onChanged();
    } catch (error) {
      setMessage(
        error instanceof Error ? error.message : "Unable to archive the story",
      );
    } finally {
      setBusyAction(null);
    }
  }

  return (
    <article className="story-detail">
      <header className="detail-header">
        <div className="detail-kicker">
          <span className={`status-pill status-${story.status}`}>
            {story.status.replace("_", " ")}
          </span>
          <span>{Math.round(story.confidence * 100)}% confidence</span>
          <span>{story.article_count} articles</span>
        </div>
        <h1>{story.headline}</h1>
        <div className="primary-source-slot">
          {(activeBundle?.articles ?? [])[0] ? (
            <a
              className="primary-source-link"
              href={activeBundle?.articles[0].url}
              target="_blank"
              rel="noreferrer"
            >
              {activeBundle?.articles[0].source_domain}
              <ExternalLink aria-hidden="true" />
            </a>
          ) : null}
        </div>
        <div className="detail-actions">
          <button
            type="button"
            className="button-secondary"
            onClick={generate}
            disabled={busyAction === "regenerate"}
          >
            <Sparkles aria-hidden="true" />
            {busyAction === "regenerate" ? "Generating…" : "Generate"}
          </button>
          <button
            type="button"
            className="button-quiet"
            onClick={archive}
            disabled={busyAction === "archive"}
          >
            <Archive aria-hidden="true" />
            Archive
          </button>
        </div>
        <div className="detail-message-slot" aria-live="polite">
          {message ? <p className="inline-message">{message}</p> : null}
        </div>
      </header>

      <section className="detail-section">
        <div className="section-heading">
          <h2>Sources</h2>
          <span>
            {story.primary_source_count > 0
              ? `${story.primary_source_count} primary source`
              : `${story.source_count} independent domains`}
          </span>
        </div>
        <div className="source-links">
          {(activeBundle?.articles ?? []).map((article) => (
            <a
              key={article.id}
              href={article.url}
              target="_blank"
              rel="noreferrer"
            >
              <strong>{article.title}</strong>
              <span>
                {article.source_domain}
                {" · "}
                {formatDate(article.published_at)}
                <ExternalLink aria-hidden="true" />
              </span>
            </a>
          ))}
        </div>
      </section>

      <section className="detail-section">
        <div className="section-heading">
          <h2>Tweet options</h2>
          <span>Copy and post manually</span>
        </div>
        <div className="draft-grid">
          {displayedDrafts.length === 0 ? (
            <div className="draft-empty">
              <p>Press Generate above to create short and long options.</p>
            </div>
          ) : (
            displayedDrafts.map((draft) => {
              const body = draftBodies[draft.id] ?? draft.body;
              const isEditing = editingDraftId === draft.id;
              return (
                <section className="draft-card" key={draft.id}>
                  <div className="draft-card-header">
                    <div>
                      <strong>{variantLabels[draft.variant]}</strong>
                      <span>
                        {draft.provider}
                        {draft.model ? ` · ${draft.model}` : ""}
                        {` · generation ${draft.generation}`}
                      </span>
                    </div>
                  </div>
                  <textarea
                    value={body}
                    rows={draft.variant === "long" ? 8 : 4}
                    maxLength={draft.variant === "long" ? 1200 : 280}
                    readOnly={!isEditing}
                    aria-label={`${variantLabels[draft.variant]} draft`}
                    onChange={(event) =>
                      setDraftBodies((current) => ({
                        ...current,
                        [draft.id]: event.target.value,
                      }))
                    }
                  />
                  <div className="draft-footer">
                    <span>
                      {body.length}/{draft.variant === "long" ? 1200 : 280}
                    </span>
                    <div>
                      <button
                        type="button"
                        className={isEditing ? "is-editing" : ""}
                        onClick={() => {
                          if (isEditing) {
                            void finishEditing(draft);
                          } else {
                            setEditingDraftId(draft.id);
                            setMessage("");
                          }
                        }}
                        disabled={busyAction === `save-${draft.id}`}
                      >
                        {isEditing ? (
                          <Check aria-hidden="true" />
                        ) : (
                          <Pencil aria-hidden="true" />
                        )}
                        {isEditing ? "Done" : "Edit"}
                      </button>
                      <button type="button" onClick={() => void copy(draft)}>
                        <Clipboard aria-hidden="true" />
                        Copy
                      </button>
                    </div>
                  </div>
                </section>
              );
            })
          )}
        </div>
      </section>
    </article>
  );
}
